import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Bank account state for individual investor
 * Stores banking information for fund transfers
 */
export interface BankSliceIndState {
  bankName: string | null;
  accountNumber: string | null;
  accountType: string | null;
  swiftCode: string | null;
  accountHolderName: string | null;
  bankAddress: string | null;
  bankCountry: string | null;
  currency: string | null;
}

const initialState: BankSliceIndState = {
  bankName: null,
  accountNumber: null,
  accountType: null,
  swiftCode: null,
  accountHolderName: null,
  bankAddress: null,
  bankCountry: null,
  currency: null,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof BankSliceIndState>(field: K) => {
  return (state: BankSliceIndState, action: PayloadAction<BankSliceIndState[K]>) => {
    state[field] = action.payload;
  };
};

const bankSliceInd = createSlice({
  name: "bankInd",
  initialState,
  reducers: {
    // Bank account field setters - Unwrapped payload pattern
    setBankNameRedux: createFieldSetter("bankName"),
    setAccountNoRedux: createFieldSetter("accountNumber"),
    setAccountTypeRedux: createFieldSetter("accountType"),
    setSwiftCodeReduct: createFieldSetter("swiftCode"),
    setAccountHolderNameRedux: createFieldSetter("accountHolderName"),
    setBankAddressRedux: createFieldSetter("bankAddress"),
    setBankCountryRedux: createFieldSetter("bankCountry"),
    setCurrencyRedux: createFieldSetter("currency"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateBankState: (state, action: PayloadAction<Partial<BankSliceIndState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetBankState: () => initialState,
  },
});

export const {
  setBankNameRedux,
  setAccountNoRedux,
  setAccountTypeRedux,
  setSwiftCodeReduct,
  setAccountHolderNameRedux,
  setBankAddressRedux,
  setBankCountryRedux,
  setCurrencyRedux,
  hydrateBankState,
  resetBankState,
} = bankSliceInd.actions;

export default bankSliceInd.reducer;
